/** Battle animation: BattleEvents in, a timeline out, and a driver that plays it.
 *
 * Two halves, kept apart on purpose.
 *
 *   `planFromEvents` is a pure function from the kernel's event list to a list
 *   of `AnimStep`. It decides nothing (I10) - it reads what already happened and
 *   says how long to spend showing it. Being pure, it is the half worth testing.
 *
 *   `BattleAnimator` plays that list against an `AnimTarget`, which is the only
 *   thing it knows about the scene. No renderer type appears in this file, so
 *   the timeline can be exercised in Node without a canvas.
 *
 * The animator has three ways to finish and all three land on the same frame:
 * playing out, `skip()`, and an aborted signal. `enabled === false` is the
 * fourth - `animate()` then applies every step's terminal state synchronously
 * and resolves before returning, because the screenshot harness needs a settled
 * frame and must never be asked to wait for one.
 */

import { hexCol, hexRow } from "@game/core";
import type { GameEvent } from "@game/core";
import { MELEE_WINDUP_POSE, RESTING_POSE } from "./keys.js";
import type { PoseName, UnitView } from "./keys.js";

// -------------------------------------------------------------------- steps

export type AnimStep =
  | { readonly kind: "turn"; readonly stack: number }
  | { readonly kind: "round"; readonly round: number }
  | { readonly kind: "move"; readonly stack: number; readonly path: readonly number[] }
  | {
      readonly kind: "attack";
      readonly attacker: number;
      readonly target: number;
      readonly damage: number;
      readonly killed: number;
      readonly ranged: boolean;
      readonly retaliation: boolean;
    }
  | { readonly kind: "death"; readonly stack: number }
  | {
      readonly kind: "spell";
      readonly side: 0 | 1;
      readonly spell: string;
      readonly target: number;
      readonly affected: readonly number[];
    }
  | { readonly kind: "mood"; readonly stack: number; readonly luck: boolean; readonly good: boolean }
  | { readonly kind: "wall"; readonly segment: number; readonly integrity: number }
  | { readonly kind: "ended"; readonly winner: 0 | 1 | null; readonly reason: string };

/** Milliseconds a stack spends crossing one hex. The classic's walk is unhurried. */
export const MS_PER_HEX = 170;
export const MS_ATTACK = 520;
export const MS_SHOOT = 640;
export const MS_DEATH = 620;
export const MS_SPELL = 720;
export const MS_MOOD = 900;
export const MS_WALL = 420;

/**
 * Steps per second of the walk cycle, and the reason it is not "one per hex".
 *
 * The stance used to alternate once per hex travelled, so a stack's gait was a
 * function of how far it was walking: one pose change for a one-hex step, seven
 * for a seven-hex charge at the same speed. A gait is a property of the
 * creature, not of the journey.
 *
 * **Twelve, not six.** Six is one stance change every 167ms against a 170ms
 * hex, so the first change of a one-hex step falls 3ms before the step ends -
 * measured, a one-hex move drew the same frame for all 24 samples of its walk
 * and only changed pose in its terminal state. A creature that crosses a hex
 * without moving its legs is gliding, which is the thing this constant exists
 * to stop. Twelve puts two frames in every hex at the walking speed and keeps
 * the cadence when a fast stack crosses two hexes in the same time; 83ms a
 * frame is 12fps, which is about what the classic's own walk cycles run at.
 */
export const WALK_STEPS_PER_SEC = 12;

/**
 * Where in an attack the blow lands, as a fraction of the step.
 *
 * Everything the player reads off an attack - the damage number, the target's
 * flinch, the white flash, the casualty count - happens on this frame and not
 * at the start of the step, because a number that appears before the weapon
 * arrives reads as a UI event rather than as a hit. Melee lands earlier than a
 * shot: the shot has to spend the first half of its step in flight.
 */
export const IMPACT_MELEE = 0.42;
export const IMPACT_RANGED = 0.55;

/**
 * The three phases of a melee swing, as fractions of the step, and why the
 * contact one is not the impact frame.
 *
 * A blow has to read as wind-up, contact and recovery, and each has to be on
 * screen long enough to be read as a pose rather than as a flicker. The poses
 * used to be cut at `wind` and again at `IMPACT_MELEE`, which over a 520ms step
 * put the `attack` column - the one frame in the whole sheet that shows the
 * weapon arriving - on screen for **62ms**, against 156ms of wind-up and 302ms
 * of follow-through. At 60Hz that is under four refreshes, and it is the frame
 * the whole animation exists to show: the shortest of the three phases was the
 * only one that mattered.
 *
 * So contact is *held across* the impact rather than ending on it. The damage
 * still lands on `IMPACT_MELEE`, which is what `battle-anim-scale.spec.ts`
 * pins; what changed is that the creature is still holding the contact pose
 * when it does. Over the same 520ms step the three phases are now 135ms /
 * 156ms / 229ms.
 */
export const MELEE_WIND = 0.26;
export const MELEE_CONTACT_END = 0.56;

export function stepDuration(step: AnimStep): number {
  switch (step.kind) {
    case "move": return Math.max(1, step.path.length) * MS_PER_HEX;
    // A retaliation is a beat inside someone else's turn, so it plays shorter.
    case "attack": return Math.round((step.ranged ? MS_SHOOT : MS_ATTACK) * (step.retaliation ? 0.7 : 1));
    case "death": return MS_DEATH;
    case "spell": return MS_SPELL;
    case "mood": return MS_MOOD;
    case "wall": return MS_WALL;
    case "turn": case "round": case "ended": return 0;
  }
}

/**
 * The kernel emits every battle event into the same stream as the adventure
 * events, so this filters as it converts. `BattleStackMoved.path` excludes the
 * hex the stack started on; the animator snapshots that from the scene the
 * moment it is handed the plan - see `BattleAnimator.shown`.
 */
export function planFromEvents(events: readonly GameEvent[]): readonly AnimStep[] {
  const out: AnimStep[] = [];
  for (const e of events) {
    switch (e.type) {
      case "BattleStackTurn":
        out.push({ kind: "turn", stack: e.stack });
        break;
      case "BattleRoundStarted":
        out.push({ kind: "round", round: e.round });
        break;
      case "BattleStackMoved":
        if (e.path.length > 0) out.push({ kind: "move", stack: e.stack, path: e.path });
        break;
      case "BattleAttacked":
        out.push({
          kind: "attack", attacker: e.attacker, target: e.target, damage: e.damage,
          killed: e.killed, ranged: e.ranged, retaliation: e.retaliation,
        });
        break;
      case "BattleStackDied":
        out.push({ kind: "death", stack: e.stack });
        break;
      case "BattleSpellCast":
        out.push({
          kind: "spell", side: e.side, spell: e.spell as string,
          target: e.target, affected: e.affected,
        });
        break;
      case "BattleMorale":
        out.push({ kind: "mood", stack: e.stack, luck: false, good: e.good });
        break;
      case "BattleLuck":
        out.push({ kind: "mood", stack: e.stack, luck: true, good: e.good });
        break;
      case "BattleWallHit":
        out.push({ kind: "wall", segment: e.segment, integrity: e.integrity });
        break;
      case "BattleEnded":
        out.push({ kind: "ended", winner: e.winner, reason: e.kind });
        break;
      default:
        break;
    }
  }
  return out;
}

/** Total wall-clock length of a plan, in milliseconds. */
export function planDuration(plan: readonly AnimStep[]): number {
  let total = 0;
  for (const s of plan) total += stepDuration(s);
  return total;
}

// ------------------------------------------------------------------- target

/**
 * Everything the animator is allowed to touch. The scene implements it; the
 * animator never reaches past it, which is what keeps this file free of both
 * pixi and the DOM.
 */
export interface AnimTarget {
  /** The hex the scene is currently *showing* the stack on, not the kernel's. */
  hexOf(stack: number): number;
  /** Park a stack on a hex and clear any interpolated offset. */
  setHex(stack: number, hex: number): void;
  /** Draw a stack at an arbitrary point on its walk. */
  setWalkPoint(stack: number, fromHex: number, toHex: number, t: number): void;
  setPose(stack: number, pose: PoseName): void;
  setView(stack: number, view: UnitView): void;
  setFacing(stack: number, facing: number): void;
  /** 0..1 white-hot overlay on a stack that has just been hit. */
  setFlash(stack: number, amount: number): void;
  setAlpha(stack: number, alpha: number): void;
  /** Recoil, in arena pixels, relative to the stack's resting position. */
  setNudge(stack: number, dx: number, dy: number): void;
  /** 0..1 death collapse: the body goes down rather than blinking out. */
  setCollapse(stack: number, t: number): void;
  /** A number that rises off the stack and fades. */
  floater(stack: number, text: string, kind: "damage" | "kill" | "heal"): void;
  banner(stack: number, kind: "morale" | "luck", good: boolean): void;
  /** An arrow or bolt in flight, `t` in 0..1; called with t<0 to clear. */
  projectile(fromHex: number, toHex: number, t: number): void;
  /** A spell effect centred on a hex, `t` in 0..1; called with t<0 to clear. */
  spellFx(hex: number, spell: string, t: number): void;
  setWall(segment: number, integrity: number): void;
  removeStack(stack: number): void;
  /** Drop every transient channel and return to the resting frame. */
  settle(): void;
}

export interface AnimatorOptions {
  /** False freezes every animation at its resting frame (the L6 requirement). */
  readonly enabled?: boolean;
  /** Multiplier on every duration; 2 is half speed. */
  readonly speed?: number;
}

interface Abortable {
  readonly aborted: boolean;
}

/** Every stack a step is about, so the animator can snapshot their positions. */
function stacksIn(step: AnimStep): readonly number[] {
  switch (step.kind) {
    case "turn": case "move": case "death": case "mood": return [step.stack];
    case "attack": return [step.attacker, step.target];
    case "spell": return step.affected;
    default: return [];
  }
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
/** Ease-out, so a walk step lands rather than stopping dead. */
const easeOut = (t: number): number => 1 - (1 - t) * (1 - t);

// ----------------------------------------------------------------- animator

export class BattleAnimator {
  private readonly target: AnimTarget;
  private plan: readonly AnimStep[] = [];
  private index = 0;
  private elapsed = 0;
  private resolve: (() => void) | null = null;
  private signal: Abortable | null = null;
  private started = false;
  /** Latch so a one-shot inside a step fires once; reset at every step boundary. */
  private floated = false;
  /**
   * Where each stack in the plan *was* when the plan arrived, and where the
   * animator has walked it to since.
   *
   * This exists because of a defect that made the walk invisible for the whole
   * life of the battle screen. The app's flow is: run the command, `setState`,
   * then `animate(events)`. React batches the state update, so `animate` is
   * called while the scene still shows the pre-command board - but the animator
   * only read `target.hexOf` at the first *tick*, one frame later, by which
   * time React had re-rendered and `stacks.update` had already parked the stack
   * on its destination hex. Every walk therefore interpolated from the
   * destination to the destination: the stack teleported, and the six frames of
   * gait played on the spot.
   *
   * Reading the scene at all was the mistake. The animator is replaying a list
   * of things that already happened, so it should carry its own idea of where
   * each stack is being *shown*: seeded once from the scene at handover, then
   * advanced by the move steps it plays. Nothing the app does to the scene in
   * between can move it.
   */
  private readonly shown = new Map<number, number>();
  enabled: boolean;
  speed: number;

  constructor(target: AnimTarget, opts?: AnimatorOptions) {
    this.target = target;
    this.enabled = opts?.enabled ?? true;
    this.speed = opts?.speed ?? 1;
  }

  get busy(): boolean {
    return this.resolve !== null;
  }

  /** Steps left to play, for a debug read-out. */
  get pending(): number {
    return this.plan.length - this.index;
  }

  animate(events: readonly GameEvent[], signal?: Abortable): Promise<void> {
    const plan = planFromEvents(events);
    if (this.resolve !== null) this.finishCurrent();
    // Snapshot now, while the scene is still showing the pre-command board.
    this.shown.clear();
    for (const step of plan) {
      for (const slot of stacksIn(step)) {
        if (!this.shown.has(slot)) this.shown.set(slot, this.target.hexOf(slot));
      }
    }
    if (!this.enabled || plan.length === 0 || signal?.aborted === true) {
      for (const s of plan) this.applyTerminal(s);
      this.target.settle();
      return Promise.resolve();
    }
    this.plan = plan;
    this.index = 0;
    this.elapsed = 0;
    this.started = false;
    this.signal = signal ?? null;
    return new Promise<void>((resolve) => {
      this.resolve = resolve;
    });
  }

  /** Jump to the end of the current plan. Bound to right-click and to Escape. */
  skip(): void {
    if (this.resolve === null) return;
    this.finishCurrent();
  }

  /** Advance by `dtMs` of wall clock. Called from the render loop. */
  tick(dtMs: number): void {
    if (this.resolve === null) return;
    if (this.signal?.aborted === true) { this.finishCurrent(); return; }

    let budget = dtMs / (this.speed <= 0 ? 1 : this.speed);
    let guard = 0;
    while (budget > 0 && this.index < this.plan.length && guard++ < 64) {
      const step = this.plan[this.index]!;
      if (!this.started) { this.begin(step); this.started = true; }
      const dur = stepDuration(step);
      if (dur <= 0) { this.applyTerminal(step); this.next(); continue; }
      const remaining = dur - this.elapsed;
      if (budget < remaining) {
        this.elapsed += budget;
        budget = 0;
        this.frame(step, this.elapsed / dur);
      } else {
        budget -= remaining;
        this.applyTerminal(step);
        this.next();
      }
    }
    if (this.index >= this.plan.length) this.finishCurrent();
  }

  /** Where the animator is showing a stack, not where the kernel has put it. */
  private hexOf(stack: number): number {
    const h = this.shown.get(stack);
    return h === undefined ? this.target.hexOf(stack) : h;
  }

  private next(): void {
    this.index++;
    this.elapsed = 0;
    this.started = false;
  }

  private finishCurrent(): void {
    for (let i = this.index; i < this.plan.length; i++) this.applyTerminal(this.plan[i]!);
    this.plan = [];
    this.index = 0;
    this.elapsed = 0;
    this.started = false;
    this.signal = null;
    this.shown.clear();
    this.target.settle();
    const r = this.resolve;
    this.resolve = null;
    if (r !== null) r();
  }

  // ---- per-step behaviour

  private begin(step: AnimStep): void {
    const t = this.target;
    this.floated = false;
    switch (step.kind) {
      case "move": {
        const from = this.hexOf(step.stack);
        const last = step.path[step.path.length - 1]!;
        // A stack that walks toward the back of the field turns its back on the
        // camera: that is what the sheet's second row is for.
        t.setView(step.stack, hexRow(last) < hexRow(from) ? "rear" : "front");
        const dx = hexCol(last) - hexCol(from);
        if (dx !== 0) t.setFacing(step.stack, dx > 0 ? 1 : -1);
        break;
      }
      case "attack": {
        // A shooter opens on the readied draw; `frame` takes it through the
        // rest. One that opened on `special` had already loosed before the
        // wind-up, so the release read as the recovery. A melee attacker opens
        // on the crouch instead, because the readied stance is now the pose it
        // is *already standing in* - see `MELEE_WINDUP_POSE`.
        t.setPose(step.attacker, step.ranged ? "ready" : MELEE_WINDUP_POSE);
        const dx = hexCol(this.hexOf(step.target)) - hexCol(this.hexOf(step.attacker));
        if (dx !== 0) t.setFacing(step.attacker, dx > 0 ? 1 : -1);
        break;
      }
      case "death":
        t.setPose(step.stack, "defend");
        t.setCollapse(step.stack, 0);
        break;
      case "mood":
        t.banner(step.stack, step.luck ? "luck" : "morale", step.good);
        break;
      default:
        break;
    }
  }

  private frame(step: AnimStep, u: number): void {
    const t = this.target;
    switch (step.kind) {
      case "move": {
        const n = step.path.length;
        const raw = u * n;
        const seg = Math.min(n - 1, Math.floor(raw));
        const local = raw - seg;
        const from = seg === 0 ? this.hexOf(step.stack) : step.path[seg - 1]!;
        const to = step.path[seg]!;
        const dx = hexCol(to) - hexCol(from);
        if (dx !== 0) t.setFacing(step.stack, dx > 0 ? 1 : -1);
        // Two-frame walk cycle. The sheets have no authored gait, so the stance
        // alternates between the two resting poses at a fixed cadence in *time*
        // rather than once per hex - see `WALK_STEPS_PER_SEC` - and the body
        // rides a hop the scene sizes to the creature.
        const elapsedMs = u * stepDuration(step);
        const stride = Math.floor((elapsedMs * WALK_STEPS_PER_SEC) / 1000);
        t.setPose(step.stack, (stride & 1) === 0 ? RESTING_POSE : "idle");
        // Eased per hex, not over the whole walk: a stack crossing seven hexes
        // accelerates once and lands once, and every hex between them is at a
        // constant speed, which is what makes a walk read as a walk.
        t.setWalkPoint(step.stack, from, to, n === 1 ? easeOut(local) : local);
        break;
      }
      case "attack": {
        const impact = step.ranged ? IMPACT_RANGED : IMPACT_MELEE;
        const dir = hexCol(this.hexOf(step.target)) >= hexCol(this.hexOf(step.attacker)) ? 1 : -1;
        if (step.ranged) {
          // Draw, loose, follow through. The arrow is in the air for the whole
          // approach and is cleared on the frame it arrives.
          if (u < impact * 0.34) t.setPose(step.attacker, "ready");
          else if (u < impact) t.setPose(step.attacker, "special");
          else t.setPose(step.attacker, "attackAlt");
          const flight = clamp01((u - impact * 0.34) / (impact * 0.66));
          if (u >= impact) t.projectile(0, 0, -1);
          else if (u >= impact * 0.34) t.projectile(this.hexOf(step.attacker), this.hexOf(step.target), flight);
        } else {
          // Wind up away from the target, then lunge into it, then recover.
          // A pure lean-in with no wind-up reads as a stumble: there is nothing
          // to say the blow was thrown rather than fallen into.
          const wind = MELEE_WIND;
          const lean = u < wind
            ? -0.42 * Math.sin((u / wind) * Math.PI)
            : u < impact
              ? easeOut((u - wind) / (impact - wind))
              : 1 - easeOut((u - impact) / (1 - impact));
          // The pose cuts are NOT the lean's cuts: the body peaks its lunge on
          // the impact frame while the contact pose is held past it into the
          // start of the recovery. See `MELEE_CONTACT_END`.
          if (u < wind) t.setPose(step.attacker, MELEE_WINDUP_POSE);
          else if (u < MELEE_CONTACT_END) t.setPose(step.attacker, "attack");
          else t.setPose(step.attacker, "attackAlt");
          t.setNudge(step.attacker, dir * 16 * lean, -3 * Math.max(0, lean));
        }
        if (u >= impact) {
          const after = (u - impact) / (1 - impact);
          // The struck stack braces and is knocked back along the line of the
          // blow. `defend` is the sheet's crouch, which is what a creature
          // taking a hit is doing; it was only ever used for the DEFEND order.
          t.setPose(step.target, "defend");
          t.setFlash(step.target, 1 - after);
          const recoil = Math.sin(after * Math.PI) * 9;
          t.setNudge(step.target, dir * recoil, -recoil * 0.25);
          if (!this.floated) {
            this.floated = true;
            t.floater(step.target, `-${step.damage}`, "damage");
            if (step.killed > 0) t.floater(step.target, `${step.killed} slain`, "kill");
          }
        }
        break;
      }
      case "death": {
        // Down, then out. Fading a standing creature in place reads as a
        // dissolve; the classic drops the body and lets it go on the way down. The
        // collapse runs ahead of the fade so there is a body on the ground to
        // see before the alpha takes it.
        const fall = easeOut(clamp01(u / 0.55));
        t.setCollapse(step.stack, fall);
        t.setAlpha(step.stack, 1 - clamp01((u - 0.45) / 0.55));
        t.setNudge(step.stack, 0, fall * 4);
        break;
      }
      case "spell": {
        t.spellFx(step.target, step.spell, u);
        const f = Math.sin(clamp01((u - 0.3) / 0.7) * Math.PI);
        for (const a of step.affected) t.setFlash(a, f);
        break;
      }
      case "wall":
        if (u >= 0.35 && !this.floated) {
          this.floated = true;
          t.setWall(step.segment, step.integrity);
        }
        break;
      default:
        break;
    }
  }

  private applyTerminal(step: AnimStep): void {
    const t = this.target;
    this.floated = false;
    switch (step.kind) {
      case "move": {
        const last = step.path[step.path.length - 1];
        if (last !== undefined) {
          const from = this.hexOf(step.stack);
          const dx = hexCol(last) - hexCol(from);
          if (dx !== 0) t.setFacing(step.stack, dx > 0 ? 1 : -1);
          // The animator's own idea of where the stack is moves with it, so a
          // strike in the same plan leans from where the walk left it.
          this.shown.set(step.stack, last);
          t.setHex(step.stack, last);
        }
        t.setView(step.stack, "front");
        t.setPose(step.stack, RESTING_POSE);
        break;
      }
      case "attack":
        t.setPose(step.attacker, RESTING_POSE);
        t.setPose(step.target, RESTING_POSE);
        t.setNudge(step.attacker, 0, 0);
        t.setNudge(step.target, 0, 0);
        t.setFlash(step.target, 0);
        t.projectile(0, 0, -1);
        break;
      case "death":
        t.setCollapse(step.stack, 1);
        t.setAlpha(step.stack, 0);
        t.setNudge(step.stack, 0, 0);
        t.removeStack(step.stack);
        break;
      case "spell":
        t.spellFx(step.target, step.spell, -1);
        for (const a of step.affected) t.setFlash(a, 0);
        break;
      case "wall":
        t.setWall(step.segment, step.integrity);
        break;
      case "turn": case "round": case "mood": case "ended":
        break;
    }
  }
}
